import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Message as MessageIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import { smartFormatTimestamp } from '../utils/dateTimeHelper';

const MessageScreen = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [transactUsers, setTransactUsers] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadData(1);
  }, []);

  const loadData = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school-common/message/get-user-list-from-message-transact?PageNumber=${page}&PageSize=10`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const newData = response.data.data || [];

      if (page === 1) {
        setTransactUsers(newData);
      } else {
        setTransactUsers((prev) => [...prev, ...newData]);
      }

      setPageNumber(page + 1);
      setHasMore(newData.length === 10);
    } catch (error) {
      console.error('MessageScreen loadData error:', error);
      handleCatch(error);
      if (page === 1) setTransactUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setPageNumber(1);
    setHasMore(true);
    await loadData(1);
  }, []);

  const loadMore = () => {
    if (!loading && hasMore) {
      loadData(pageNumber);
    }
  };

  const formatDate = (dateString) => {
    return smartFormatTimestamp(dateString);
  };

  const getMessageTypeConfig = (type) => {
    if (type === 'S') {
      return {
        label: 'SENT',
        color: '#4CAF50',
        backgroundColor: '#E8F5E8',
        icon: <ArrowUpwardIcon sx={{ fontSize: 14 }} />,
      };
    } else {
      return {
        label: 'RECEIVED',
        color: '#2196F3',
        backgroundColor: '#E3F2FD',
        icon: <ArrowDownwardIcon sx={{ fontSize: 14 }} />,
      };
    }
  };

  const EmptyState = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8, px: 3 }}>
      <MessageIcon sx={{ fontSize: 64, color: '#E0E0E0', mb: 2 }} />
      <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1, color: '#333' }}>No Messages Yet</Typography>
      <Typography sx={{ fontSize: 13, color: '#888', textAlign: 'center', mb: 3 }}>
        Your conversation history will appear here.
      </Typography>
      <Button variant="outlined" startIcon={<RefreshIcon />} onClick={onRefresh} sx={{ borderRadius: 20 }}>
        Check for Messages
      </Button>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader
        title="Messages"
        showBack={true}
        rightAction={
          <IconButton onClick={onRefresh} disabled={loading} sx={{ color: '#fff' }}>
            <RefreshIcon />
          </IconButton>
        }
      />

      <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', backgroundColor: '#F5F7FA' }}>
        {loading && transactUsers.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : transactUsers.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Box sx={{ backgroundColor: '#fff', mt: 1, mx: 1, borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              {transactUsers.map((item, index) => {
                const isSent = item.type === 'S';
                const initials = (item.userName || '?')[0].toUpperCase();
                return (
                  <React.Fragment key={index}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', '&:active': { bgcolor: '#f5f5f5' } }}
                      onClick={() => navigate('/message-details', { state: { username: item.userName } })}
                    >
                      {/* Unread indicator dot */}
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isSent ? '#4CAF50' : colors.BLUE, mr: 1.5, flexShrink: 0 }} />

                      {/* Avatar */}
                      <Avatar sx={{ width: 44, height: 44, bgcolor: isSent ? '#E8F5E9' : '#E3F2FD', color: isSent ? '#2E7D32' : colors.BLUE, fontWeight: 700, mr: 1.5, flexShrink: 0 }}>
                        {initials}
                      </Avatar>

                      {/* Text */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#222', lineHeight: 1.3 }}>
                          {item.userName}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {isSent ? '↑ ' : '↓ '}{item.lastMessage}
                        </Typography>
                      </Box>

                      {/* Right: time + chevron */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 1, flexShrink: 0 }}>
                        <Typography sx={{ fontSize: 11, color: '#aaa', mb: 0.5 }}>
                          {formatDate(item.transactDate)}
                        </Typography>
                        <ChevronRightIcon sx={{ fontSize: 16, color: '#ccc' }} />
                      </Box>
                    </Box>
                    {index < transactUsers.length - 1 && <Divider sx={{ ml: '72px' }} />}
                  </React.Fragment>
                );
              })}
            </Box>

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Button variant="outlined" onClick={loadMore} disabled={loading} sx={{ borderRadius: 20, minWidth: 160 }}>
                  {loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessageScreen;
